import { useEffect, useState, type FormEvent } from 'react';
import {
  makeStyles,
  Button,
  Typography,
  TextField,
  IconButton,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  discoveryApiRef,
  errorApiRef,
  microsoftAuthApiRef,
  useAnalytics,
  useApi,
  type BackstageUserIdentity,
  type SignInPageProps,
} from '@backstage/core-plugin-api';
import { Progress, UserIdentity } from '@backstage/core-components';
import techneMark from '../nav/assets/techne-mark.png';
import {
  createExternalUserIdentity,
  fetchExternalSession,
  loginExternalUser,
} from '../externalUsers/api';
import type { ExternalUser } from '../externalUsers/types';

const GROUP_REQUIRED_MESSAGE =
  'Seu usuário não possui nenhum grupo autorizado no Developer Portal. Solicite acesso ao time de DevOps.';

type SignInView = 'providers' | 'email';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 20px',
    boxSizing: 'border-box',
    background:
      'radial-gradient(ellipse 90% 70% at 50% 0%, #1a1a7a 0%, #0b0b3a 45%, #07071f 100%)',
    fontFamily:
      '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  stage: {
    width: '100%',
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 28,
    animation: '$fadeUp 520ms ease-out both',
  },
  '@keyframes fadeUp': {
    from: { opacity: 0, transform: 'translateY(14px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  mark: {
    width: 96,
    height: 96,
    borderRadius: 22,
    boxShadow: '0 18px 48px rgba(0, 0, 0, 0.45)',
    display: 'block',
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'center',
  },
  title: {
    color: '#ffffff',
    fontSize: 34,
    fontWeight: 600,
    letterSpacing: '-0.03em',
    lineHeight: 1.15,
    margin: 0,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 16,
    lineHeight: 1.5,
    maxWidth: 340,
    margin: 0,
  },
  actions: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 8,
  },
  primary: {
    height: 48,
    borderRadius: 10,
    textTransform: 'none',
    fontSize: 15,
    fontWeight: 600,
    backgroundColor: '#ffffff',
    color: '#0b0b3a',
    boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
    '&:hover': {
      backgroundColor: '#f0f0ff',
    },
    '&:disabled': {
      backgroundColor: 'rgba(255,255,255,0.55)',
      color: 'rgba(11,11,58,0.55)',
    },
  },
  secondary: {
    height: 44,
    borderRadius: 10,
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.88)',
    borderColor: 'rgba(255,255,255,0.28)',
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.55)',
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  },
  emailHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: -8,
  },
  backButton: {
    color: 'rgba(255,255,255,0.72)',
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: '#ffffff',
    },
  },
  emailTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    flex: 1,
    textAlign: 'left',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
  },
  field: {
    '& .MuiInputBase-root': {
      color: '#ffffff',
      backgroundColor: 'rgba(255,255,255,0.06)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.18)',
      transition: 'border-color 160ms ease, background-color 160ms ease',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.09)',
        borderColor: 'rgba(255,255,255,0.32)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderColor: 'rgba(255,255,255,0.55)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.55)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'rgba(255,255,255,0.82)',
    },
    '& .MuiInputBase-input': {
      padding: '14px 14px',
      fontSize: 15,
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255,255,255,0.35)',
      opacity: 1,
    },
  },
  error: {
    color: '#ffb4b4',
    fontSize: 13,
    marginTop: 4,
  },
  footer: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: 12,
    marginTop: 8,
  },
  loading: {
    width: '100%',
    maxWidth: 280,
  },
  loadingLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    marginTop: 16,
  },
});

const identityHasAssignedGroup = (identity: {
  ownershipEntityRefs?: string[];
}): boolean =>
  (identity.ownershipEntityRefs ?? []).some(ref =>
    String(ref).toLowerCase().startsWith('group:'),
  );

export function TechneSignInPage(props: SignInPageProps) {
  const classes = useStyles();
  const authApi = useApi(microsoftAuthApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const errorApi = useApi(errorApiRef);
  const analytics = useAnalytics();
  const [view, setView] = useState<SignInView>('providers');
  const [checkingSession, setCheckingSession] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const completeMicrosoftSignIn = async (identity: BackstageUserIdentity) => {
    if (!identityHasAssignedGroup(identity)) {
      await (
        authApi as unknown as { signOut?: () => Promise<void> }
      ).signOut?.();
      throw new Error(GROUP_REQUIRED_MESSAGE);
    }

    const profile = await authApi.getProfile();
    props.onSignInSuccess(
      UserIdentity.create({
        identity,
        profile,
        authApi,
      }),
    );
  };

  const completeExternalSignIn = (user: ExternalUser) => {
    props.onSignInSuccess(createExternalUserIdentity(user, discoveryApi));
    // Ensure the external-only shell lands on the client page.
    window.history.replaceState(null, '', '/cliente-externo');
  };

  // Restore Microsoft or external session on mount.
  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        const externalUser = await fetchExternalSession(discoveryApi);
        if (!active) {
          return;
        }
        if (externalUser) {
          completeExternalSignIn(externalUser);
          analytics.captureEvent('signIn', 'external-session-restore');
          return;
        }

        const existing = await authApi.getBackstageIdentity({
          optional: true,
        });
        if (!active || !existing?.identity) {
          return;
        }

        await completeMicrosoftSignIn(existing.identity);
        analytics.captureEvent('signIn', 'microsoft-session-restore');
      } catch (err) {
        if (!active) {
          return;
        }
        await (
          authApi as unknown as { signOut?: () => Promise<void> }
        ).signOut?.();
        const raw = err instanceof Error ? err.message : '';
        if (/sem grupo/i.test(raw) || /grupo/i.test(raw)) {
          setError(GROUP_REQUIRED_MESSAGE);
        }
      } finally {
        if (active) {
          setCheckingSession(false);
        }
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once on mount
  }, [authApi, discoveryApi]);

  const signInMicrosoft = async () => {
    setBusy(true);
    setError(null);
    try {
      const identityResponse = await authApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!identityResponse) {
        throw new Error('Microsoft Entra ID is not configured for sign-in');
      }

      await completeMicrosoftSignIn(identityResponse.identity);
      analytics.captureEvent('signIn', 'microsoft');
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Sign-in failed';
      const message =
        /sem grupo/i.test(raw) || /not.*group|grupo/i.test(raw)
          ? GROUP_REQUIRED_MESSAGE
          : raw;
      setError(message);
      errorApi.post(err as Error);
    } finally {
      setBusy(false);
    }
  };

  const openEmailView = () => {
    setError(null);
    setView('email');
  };

  const backToProviders = () => {
    setError(null);
    setUsername('');
    setPassword('');
    setView('providers');
  };

  const onEmailSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = await loginExternalUser(discoveryApi, {
        email: username.trim(),
        password,
      });
      completeExternalSignIn(user);
      analytics.captureEvent('signIn', 'external-email');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Falha no login por e-mail';
      setError(message);
      errorApi.post(err as Error);
    } finally {
      setBusy(false);
    }
  };

  if (checkingSession) {
    return (
      <div className={classes.root}>
        <div className={classes.stage}>
          <div className={classes.brand}>
            <img className={classes.mark} src={techneMark} alt="Techne" />
            <Typography className={classes.title} component="h1">
              Techne
            </Typography>
          </div>
          <div className={classes.loading}>
            <Progress />
          </div>
          <Typography className={classes.loadingLabel}>
            Verificando sessão…
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.stage} key={view}>
        <div className={classes.brand}>
          <img className={classes.mark} src={techneMark} alt="Techne" />
          <Typography className={classes.title} component="h1">
            Techne
          </Typography>
          <Typography className={classes.subtitle} component="p">
            {view === 'providers'
              ? 'Developer Portal — catálogo, templates e ferramentas internas.'
              : 'Informe seu usuário e senha para continuar.'}
          </Typography>
        </div>

        {view === 'providers' ? (
          <div className={classes.actions}>
            <Button
              className={classes.primary}
              variant="contained"
              disableElevation
              disabled={busy}
              onClick={signInMicrosoft}
            >
              {busy ? 'Entrando…' : 'Entrar com Microsoft'}
            </Button>
            <Button
              className={classes.secondary}
              variant="outlined"
              disabled={busy}
              onClick={openEmailView}
            >
              Entrar com E-mail
            </Button>
          </div>
        ) : (
          <>
            <div className={classes.emailHeader}>
              <IconButton
                className={classes.backButton}
                aria-label="Voltar"
                onClick={backToProviders}
                size="small"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography className={classes.emailTitle} component="h2">
                Entrar com E-mail
              </Typography>
            </div>

            <form className={classes.form} onSubmit={onEmailSubmit} noValidate>
              <TextField
                className={classes.field}
                label="Usuário"
                variant="outlined"
                fullWidth
                autoComplete="username"
                value={username}
                onChange={event => setUsername(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                className={classes.field}
                label="Senha"
                type="password"
                variant="outlined"
                fullWidth
                autoComplete="current-password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                className={classes.primary}
                variant="contained"
                disableElevation
                type="submit"
                fullWidth
                disabled={busy}
              >
                {busy ? 'Entrando…' : 'Entrar'}
              </Button>
            </form>
          </>
        )}

        {error && (
          <Typography className={classes.error} role="alert">
            {error}
          </Typography>
        )}

        <Typography className={classes.footer}>
          {view === 'providers'
            ? 'Acesso corporativo via Microsoft Entra ID ou e-mail'
            : 'Acesso exclusivo à página Cliente externo'}
        </Typography>
      </div>
    </div>
  );
}
